import React, { useState, useMemo } from 'react';
import { Form } from 'reactstrap';
import TableBuilder, { ModalButtonBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import FlexInputBuilder from '../../../../../../../../../GlobalComponents/Builders/FlexInputBuilder';
import { capitalizeFirstLetter, buildOptionsFromArray, buildSelectOptionsFromArray, handleTabOnKeyDown, handleCopy } from '../../../../../../../../../GlobalComponents/Helpers';
import { buildExcelSheet } from '../../../../../../../../../GlobalComponents/Helpers/excel';
import CreatableMultiSelect from '../../../../../../../../../GlobalComponents/Common/Component/CreatableMultiSelect';

const SEPARATOR = ' || ';

// Keep the data-table header pinned while its container scrolls (no pagination viewer).
const stickyHeaderStyles = {
    headRow: { style: { position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'var(--bs-body-bg, #fff)' } },
};

const worksheetInstructions = [
    'Instructions:',
    '1. Fill in the "Map To" column using values from the "Available Destination Values" column.',
    '2. For composite values (Parent || Value), use the exact format shown including the " || " separator.',
    '3. Only existing values can be mapped. New values must be added to the system before mapping.',
    '4. To load mappings back, copy the Source and Map To columns and paste into the Load Mappings tool.',
];

const downloadMappingTableExcel = async (results, sourceResourceName) => {
    const sourceValues = results?.data?.sourceValues || [];
    const destValues = results?.data?.destinationValues || [];
    const hasComposites = results?.data?.hasComposites;
    const matchedById = results?.data?.matchedById;
    const destinationParentMap = results?.data?.destinationParentMap || {};
    const hasParentMap = Object.keys(destinationParentMap).length > 0;
    const hasCounts = sourceValues.some(sv => sv.count > 0);
    const maxRows = Math.max(sourceValues.length, destValues.length);

    const srcHeader = hasComposites ? 'Source (Parent || Value)' : 'Source Value';
    const mapHeader = hasComposites ? 'Map To (Parent || Value)' : 'Map To';
    const destHeader = hasComposites ? 'Available (Parent || Value)' : 'Available Destination Values';

    // Build per-row validation when parent map exists
    const rowValidation = hasParentMap ? sourceValues.map(sv => {
        const srcParent = (sv?.sourceValue || '').split(SEPARATOR)[0];
        const destParent = destinationParentMap[srcParent];
        if (destParent) return destValues.filter(dv => dv.startsWith(destParent + SEPARATOR));
        return destValues;
    }) : null;

    const rows = Array.from({ length: maxRows }, (_, i) => {
        const sv = sourceValues[i];
        const existing = sv?.existingMappings?.[0] || '';
        const row = [];
        if (hasCounts) row.push(String(sv?.count ?? ''));
        if (matchedById) row.push(String(sv?.sourceExternalId || ''));
        row.push(String(sv?.sourceValue || ''));
        row.push(String(existing), '', String(destValues[i] || ''));
        return row;
    });

    const columns = [];
    if (hasCounts) columns.push({ header: 'Count', width: 7, align: 'center' });
    if (matchedById) columns.push({ header: 'Source ID', width: 15, align: 'center' });
    columns.push({ header: srcHeader, width: 35 });
    columns.push(
        { header: mapHeader, width: 35, highlight: 'FFBA895D', highlightFont: 'FFFFFFFF', validation: hasParentMap ? undefined : destValues, rowValidation: rowValidation || undefined },
        { header: '', width: 8 },
        { header: destHeader, width: 35 },
    );

    await buildExcelSheet({
        fileName: `${sourceResourceName}_mapping_table.xlsx`,
        sheetName: 'Mapping Table',
        columns,
        rows,
        instructions: worksheetInstructions,
    });
};

// ─── Mapping Worksheet Modal ─────────────────────────────────────────

const MappingWorksheetModal = ({
    sourceResourceId,
    sourceResourceName,
    destinationResourceId,
    handleDMTAction,
    getResourceValueMappings,
    jobKeyMappings,
    jobResourceMappings,
    keyResourceLookup,
    dmtStates = [],
    savedOptions = null,
    onPersistOptions,
}) => {
    const [includeExisting, setIncludeExisting] = useState(0);   // 0 = blank, 1 = include existing
    const [includeUnused, setIncludeUnused] = useState('No');
    const [showRecordCounts, setShowRecordCounts] = useState(true);  // Data Recon on by default
    const [mappingTableResourceId, setMappingTableResourceId] = useState(null);
    const [mappingTableKey, setMappingTableKey] = useState(null);
    const [matchBy, setMatchBy] = useState('name');
    const [excludeStatuses, setExcludeStatuses] = useState([]);
    const [result, setResult] = useState(null);     // backend response, held for the in-modal viewer
    const [generating, setGenerating] = useState(false);

    const terminalCategories = ['resolved', 'cancelled'];
    const statusOptions = useMemo(() => {
        if (!dmtStates?.length) return [];
        return dmtStates
            .filter(s => s.type === 'status' && parseInt(s.is_active) === 1)
            .map(s => ({
                value: s.name,
                label: `${capitalizeFirstLetter(s.name)}${terminalCategories.includes(s.category) ? ' ⛔' : ''}`,
            }));
    }, [dmtStates]);

    const terminalStatusNames = useMemo(() => {
        if (!dmtStates?.length) return [];
        return dmtStates
            .filter(s => s.type === 'status' && parseInt(s.is_active) === 1 && terminalCategories.includes(s.category))
            .map(s => s.name);
    }, [dmtStates]);

    const keyResourceMappings = useMemo(() =>
        (jobResourceMappings?.current || []).filter(m => m.is_setup === 0)
    , [jobResourceMappings?.current?.length]);

    const lookupForResource = useMemo(() => {
        if (!keyResourceLookup?.length) return []
        const jobResourceIds = (jobResourceMappings?.current || []).map(m => m.source_resource_id)
        return keyResourceLookup.filter(l =>
            String(l.setup_resource_id) === String(sourceResourceId)
            && jobResourceIds.includes(l.key_resource_id)
        )
    }, [keyResourceLookup, sourceResourceId, jobResourceMappings?.current?.length])

    const resourceOptionsForLookup = useMemo(() => {
        if (!lookupForResource.length) return []
        const seen = new Set()
        return lookupForResource.reduce((acc, l) => {
            if (!seen.has(l.key_resource_id)) {
                seen.add(l.key_resource_id)
                acc.push({ id: l.key_resource_id, value: l.key_resource_name })
            }
            return acc
        }, [])
    }, [lookupForResource])

    const filteredKeysForResource = useMemo(() => {
        if (!mappingTableResourceId) return [];
        return lookupForResource
            .filter(l => String(l.key_resource_id) === String(mappingTableResourceId))
            .map(l => ({ source_key: l.key_name }))
    }, [mappingTableResourceId, lookupForResource]);

    // Seed the recon resource/key from the first available lookup match.
    const seedReconResource = () => {
        if (lookupForResource.length > 0) {
            const match = lookupForResource[0]
            setMappingTableResourceId(match.key_resource_id)
            setMappingTableKey(match.key_name)
        } else {
            setMappingTableResourceId(null)
            setMappingTableKey(null)
        }
    };

    // On open: restore the last-used options for this resource pair, falling back to defaults.
    const hydrateForm = () => {
        const o = savedOptions
        setIncludeExisting(o?.includeExisting ?? 0)
        setIncludeUnused(o?.includeUnused ?? 'No')
        // Default on, but never when there's nothing to reconcile against (toggle is hidden then).
        const recon = reconAvailable && (o ? o.showRecordCounts : true)
        setShowRecordCounts(recon)
        setMatchBy(o?.matchBy ?? 'name')
        setExcludeStatuses(o?.excludeStatuses ?? ['finalized'])

        // Prefer a saved resource/key, but only if it's still a valid lookup option; else reseed.
        const savedRid = o?.mappingTableResourceId
        const stillValid = savedRid != null && lookupForResource.some(l => String(l.key_resource_id) === String(savedRid))
        if (recon && stillValid) {
            setMappingTableResourceId(savedRid)
            setMappingTableKey(o?.mappingTableKey ?? null)
        } else if (recon) {
            seedReconResource()
        } else {
            setMappingTableResourceId(null)
            setMappingTableKey(null)
        }

        setResult(null)
        setGenerating(false)
    };

    // Snapshot the current options up to the parent so the next open restores them.
    // Accepts overrides because state setters are async — pass the just-changed value in.
    const persistOptions = (overrides = {}) => {
        onPersistOptions?.({
            includeExisting,
            includeUnused,
            showRecordCounts,
            mappingTableResourceId,
            mappingTableKey,
            matchBy,
            excludeStatuses,
            ...overrides,
        })
    };

    const toggleRecordCounts = (val) => {
        const enabled = val === 'Yes'
        setShowRecordCounts(enabled)
        persistOptions({ showRecordCounts: enabled })
        if (enabled) seedReconResource()
        else { setMappingTableResourceId(null); setMappingTableKey(null); }
    };

    const buildPayload = () => {
        const payload = {
            destinationResourceId,
            includeUnused: includeUnused === 'Yes',
            includeExisting: parseInt(includeExisting) === 1,
            excludeStatuses: excludeStatuses.length > 0 ? excludeStatuses : undefined,
        }
        if (showRecordCounts) {
            if (!mappingTableResourceId) { ToastBuilder('error', 'Resource Mapping is required for record counts'); return null }
            payload.extractResourceId = mappingTableResourceId
            payload.extractKey = mappingTableKey
            payload.matchBy = matchBy || 'name'
        }
        return payload
    };

    // Generate and show the data in-modal (does not download, does not close).
    const handleGenerate = () => {
        const payload = buildPayload()
        if (!payload) return
        persistOptions()
        setGenerating(true)
        handleDMTAction('create-mapping-table', sourceResourceId, (results) => {
            setGenerating(false)
            if (results?.data?.success) {
                setResult(results.data)
                getResourceValueMappings()
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to create mapping table')
            }
        }, payload)
    };

    // Download the Excel worksheet without closing the modal. Reuses the loaded
    // result when present; otherwise fetches fresh for the current options.
    const handleDownload = () => {
        if (result) {
            downloadMappingTableExcel({ data: result }, sourceResourceName)
            return
        }
        const payload = buildPayload()
        if (!payload) return
        persistOptions()
        setGenerating(true)
        handleDMTAction('create-mapping-table', sourceResourceId, (results) => {
            setGenerating(false)
            if (results?.data?.success) {
                setResult(results.data)
                downloadMappingTableExcel(results, sourceResourceName)
                getResourceValueMappings()
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to create mapping table')
            }
        }, payload)
    };

    // Two independent lists for analysis — source values (left) and available destination
    // values (right). They're unrelated sets of different lengths, so never paired by row.
    const sourceRows = useMemo(() => (result?.sourceValues || []).map((s, i) => ({
        idx: i,
        count: s?.count ?? '',
        id: s?.sourceExternalId ?? '',
        sourceValue: s?.sourceValue ?? '',
        existing: Array.isArray(s?.existingMappings) ? s.existingMappings.join(', ') : '',
    })), [result]);

    const destinationRows = useMemo(() => (result?.destinationValues || []).map((v, i) => ({
        idx: i,
        destinationValue: v ?? '',
    })), [result]);

    const hasCounts = useMemo(() => (result?.sourceValues || []).some(s => (s?.count ?? 0) > 0), [result]);

    // "Build from Records" is only offered when there's a resource (with keys) to reconcile
    // against; each lookup option always carries a key, so a non-empty list implies keys exist.
    const reconAvailable = resourceOptionsForLookup.length > 0;

    // When Build from Records is on, generating/downloading requires a chosen resource
    // and at least one key on it. Off → always allowed.
    const reconReady = !showRecordCounts || (
        !!mappingTableResourceId &&
        filteredKeysForResource.length > 0
    );

    const reconBlockReason = showRecordCounts && !reconReady
        ? (!mappingTableResourceId
            ? 'Select a resource to build from records.'
            : 'Selected resource has no keys to extract — pick another.')
        : null;

    return (
        <ModalButtonBuilder title='Mapping Worksheet' className='btn-secondary m-1 py-2'
            modalTitle={`${sourceResourceName} Mapping Worksheet`}
            style={{ maxWidth: result ? '1200px' : '620px' }}
            onClick={hydrateForm}
            onSubmit={false}
            onCancelLabel='Close'
            body={
                <div>
                    <p className='mb-1' style={{ fontSize: 13 }}>Generate a mapping worksheet with source and destination values.</p>
                    <ul className='text-muted ps-3 mb-3' style={{ fontSize: 12 }}>
                        <li>Fill in the <b>Map To</b> column using available destination values</li>
                        <li>Use <b>Load Mappings</b> in Actions to paste completed mappings back</li>
                        <li>Only existing values can be mapped — new values must be added first</li>
                    </ul>

                    <FlexInputBuilder
                        layout='grid'
                        inputSize='sm'
                        inputs={[
                            {
                                label: 'Mappings', name: 'mappingTableIncludeExisting', type: 'reactselect', size: 6,
                                value: includeExisting, onChange: (v) => { const nv = v ?? 0; setIncludeExisting(nv); persistOptions({ includeExisting: nv }); }, isSearchable: false,
                                helpText: 'Pre-fill with current mapped values',
                                options: [{ label: 'Blank Mappings', value: 0 }, { label: 'Include Existing Mappings', value: 1 }],
                            },
                            {
                                label: 'Build from Records', name: 'mappingTableRecordCounts', type: 'reactselect', size: 6,
                                visible: reconAvailable,
                                value: showRecordCounts ? 'Yes' : 'No', onChange: (v) => toggleRecordCounts(v || 'No'), isSearchable: false,
                                helpText: 'Build the worksheet from values found in staged records, with usage counts',
                                options: buildOptionsFromArray(['No', 'Yes'], { labelIsValue: true }),
                            },
                        ]} />

                    {reconAvailable && showRecordCounts && (
                        <div className='section-group-muted mt-2' style={{ borderRadius: 6, padding: '10px 14px' }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Record Source</span>
                            <FlexInputBuilder
                                layout='grid'
                                inputSize='sm'
                                inputs={[
                                    {
                                        // Chooses "used values only" (No) vs "all imported values, counts annotated" (Yes).
                                        label: 'Include All', name: 'mappingTableIncludeUnused', type: 'reactselect', size: 6,
                                        value: includeUnused, onChange: (v) => { const nv = v || 'No'; setIncludeUnused(nv); persistOptions({ includeUnused: nv }); }, isSearchable: false,
                                        helpText: 'No = only values found in records · Yes = all values, counts annotated',
                                        options: buildOptionsFromArray(['No', 'Yes'], { labelIsValue: true }),
                                    },
                                    {
                                        label: 'Resource', name: 'mappingTableResource', type: 'reactselect', size: 6,
                                        value: mappingTableResourceId, onChange: (v) => { const nv = v || null; setMappingTableResourceId(nv); setMappingTableKey(null); persistOptions({ mappingTableResourceId: nv, mappingTableKey: null }); },
                                        options: buildSelectOptionsFromArray(resourceOptionsForLookup, { label: 'value', value: 'id' }),
                                    },
                                    {
                                        label: 'Source Key', name: 'mappingTableKey', type: 'reactselect', size: 6,
                                        visible: !!mappingTableResourceId,
                                        value: mappingTableKey, onChange: (v) => { const nv = v || null; setMappingTableKey(nv); persistOptions({ mappingTableKey: nv }); },
                                        helpText: `The key containing the values for ${sourceResourceName}`,
                                        options: buildSelectOptionsFromArray(
                                            filteredKeysForResource.map(k => ({ id: k.source_key, value: k.source_key })),
                                            { label: 'value', value: 'id' }
                                        ),
                                    },
                                    {
                                        label: 'Match By', name: 'mappingTableMatchBy', type: 'reactselect', size: 6,
                                        visible: !!mappingTableResourceId,
                                        value: matchBy, onChange: (v) => { const nv = v || 'name'; setMatchBy(nv); persistOptions({ matchBy: nv }); }, isSearchable: false,
                                        helpText: 'How to match extracted values against source values',
                                        options: [
                                            { label: 'Name', value: 'name' },
                                            { label: 'Name or ID', value: 'name_or_id' },
                                            { label: 'ID Only', value: 'id' },
                                        ],
                                    },
                                ]} />
                        </div>
                    )}

                    <label className='d-block mb-2 mt-2 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Exclude Statuses</label>
                    <CreatableMultiSelect
                        value={excludeStatuses}
                        onChange={(values) => { setExcludeStatuses(values); persistOptions({ excludeStatuses: values }); }}
                        options={statusOptions}
                        placeholder='Select statuses to exclude...'
                        autoNumeric={false}
                        size='md'
                        className='react-select-tall'
                    />
                    <p className='text-muted mb-0 mt-1' style={{ fontSize: 11 }}>⛔ = terminal status. Records in these statuses are excluded from record counts. Defaults to terminal states.</p>

                    <div className='d-flex align-items-center gap-2 mt-3'>
                        <ButtonBuilder label={result ? 'Regenerate' : 'Generate'} className='btn-primary btn-sm' type='button' useSpinner={generating} onClick={handleGenerate} disabled={!reconReady || generating} />
                        <ButtonBuilder label='Download Excel' className='btn-outline-secondary btn-sm' type='button' useSpinner={false} onClick={handleDownload} disabled={!reconReady || generating} />
                    </div>
                    {reconBlockReason && <p className='text-warning mb-0 mt-1' style={{ fontSize: 11 }}>{reconBlockReason}</p>}

                    {result && (
                        <div className='mt-3 d-flex' style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {/* Left — source values (what we may need to map) */}
                            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                                <label className='d-block mb-2 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Source Values ({sourceRows.length})
                                </label>
                                <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                                    <TableBuilder
                                        subHeader={true}
                                        processing={false}
                                        pagination={false}
                                        columnsToSearch={['sourceValue', 'existing', 'id']}
                                        data={sourceRows}
                                        styles={stickyHeaderStyles}
                                        columns={[
                                            ...(hasCounts ? [{ name: 'Count', selector: (r) => r.count, sortable: true, width: '80px', center: true }] : []),
                                            { name: 'ID', selector: (r) => r.id, sortable: true, width: '110px' },
                                            { name: 'Value', selector: (r) => r.sourceValue, sortable: true, wrap: true },
                                            ...(includeExisting ? [{ name: 'Existing Mapping', selector: (r) => r.existing, sortable: true, wrap: true }] : []),
                                        ]}
                                    />
                                </div>
                            </div>

                            {/* Right — available destination values */}
                            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                                <label className='d-block mb-2 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Available Destination Values ({destinationRows.length})
                                </label>
                                <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                                    <TableBuilder
                                        subHeader={true}
                                        processing={false}
                                        pagination={false}
                                        columnsToSearch={['destinationValue']}
                                        data={destinationRows}
                                        styles={stickyHeaderStyles}
                                        columns={[
                                            { name: 'Value', selector: (r) => r.destinationValue, sortable: true, wrap: true },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};

// ─── Load Mappings Modal ─────────────────────────────────────────────

const LoadMappingsModal = ({
    sourceResourceId,
    sourceResourceName,
    actionRegister,
    actionErrors,
    actionControl,
    actionSetValue,
    handleDMTAction,
    getResourceValueMappings,
    onSkipped,
}) => {
    const [preview, setPreview] = useState(null);
    // 1-based column numbers the user reads off their paste; default to cols 1 and 2.
    const [sourceCol, setSourceCol] = useState(1);
    const [destCol, setDestCol] = useState(2);
    const [overwrite, setOverwrite] = useState(0);

    const asCol = (v, fallback) => {
        const n = parseInt(v);
        return Number.isInteger(n) && n >= 1 ? n : fallback;
    };

    const parseInput = (raw) => {
        if (!raw || !raw.trim()) return [];
        const srcCol = asCol(sourceCol, 1) - 1;
        const dstCol = asCol(destCol, 2) - 1;
        return raw.trim().split('\n')
            .map(line => {
                const parts = line.split('\t');
                const source = (parts[srcCol] || '').trim();
                const destination = (parts[dstCol] || '').trim();
                return { source, destination };
            })
            .filter(p => p.source);
    };

    // Column count of the first data line, to help the user pick indices.
    const columnCount = () => {
        const raw = actionControl._formValues?.['loadMappingsInput' + sourceResourceId] || '';
        const first = raw.trim().split('\n')[0];
        return first ? first.split('\t').length : 0;
    };

    const handlePreview = () => {
        const raw = actionControl._formValues?.['loadMappingsInput' + sourceResourceId] || '';
        const parsed = parseInput(raw);
        const toUpdate = parsed.filter(p => p.destination);
        const toSkip = parsed.filter(p => !p.destination);
        setPreview({ toUpdate, toSkip, total: parsed.length });
    };

    return (
        <ModalButtonBuilder title='Load Mappings' className='btn-outline-danger'
            formId={'load-mappings-form-' + sourceResourceId}
            modalTitle={`Load ${sourceResourceName} Mappings`}
            style={{ maxWidth: '560px' }}
            onClick={() => {
                setPreview(null)
                setSourceCol(1)
                setDestCol(2)
                setOverwrite(0)
                actionSetValue('loadMappingsInput' + sourceResourceId, '')
            }}
            body={
                <Form id={'load-mappings-form-' + sourceResourceId} onSubmit={(e) => {
                    e.preventDefault()
                    const raw = actionControl._formValues?.['loadMappingsInput' + sourceResourceId] || '';
                    const parsed = parseInput(raw).filter(p => p.destination);
                    if (!parsed.length) return ToastBuilder('error', 'No valid mappings to load')

                    handleDMTAction('load-value-mappings', sourceResourceId, (results) => {
                        console.log('load-value-mappings', results)
                        if (results?.data?.success) {
                            const updated = results?.data?.updated || 0;
                            const skipped = results?.data?.skipped || [];
                            ToastBuilder('success', `${updated} mapping(s) updated${skipped.length ? `, ${skipped.length} skipped` : ''}`)
                            if (onSkipped) onSkipped(skipped)
                            getResourceValueMappings()
                            setPreview(null)
                            actionSetValue('loadMappingsInput' + sourceResourceId, '')
                        } else {
                            ToastBuilder('error', results?.data?.message || 'Failed to load mappings')
                        }
                    }, { mappings: parsed, allowOverwrite: parseInt(overwrite) === 1 })
                }}>
            <p className='mb-1' style={{ fontSize: 13 }}>
                        Paste tab-separated rows, then point the importer at the source and destination columns.
                    </p>
                    <ul className='text-muted ps-3 mb-3' style={{ fontSize: 12 }}>
                        <li>Composite values use the <b>{SEPARATOR.trim()}</b> separator (e.g. <i>Board {SEPARATOR.trim()} Status</i>)</li>
                        <li>Only existing source and destination values are matched; unrecognized rows are skipped and listed on the resource after import</li>
                        <li>Imported mappings are set to <b>Imported</b> status (not yet approved)</li>
                    </ul>

                    {/* ── Settings ── */}
                    <label className='d-block mb-2 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Columns</label>
            <div className='d-flex align-items-center' style={{ gap: 24, marginBottom: 2 }}>
                        <div className='d-flex align-items-center' style={{ gap: 8 }}>
                            <label className='mb-0' style={{ fontSize: 12, fontWeight: 600 }}>Source Col</label>
                            <input className='form-control form-control-sm' type='text' inputMode='numeric' style={{ width: 70 }}
                                value={sourceCol} onChange={(e) => { setSourceCol(e.target.value.replace(/\D/g, '')); setPreview(null); }} />
                        </div>
                        <div className='d-flex align-items-center' style={{ gap: 8 }}>
                            <label className='mb-0' style={{ fontSize: 12, fontWeight: 600 }}>Dest Col</label>
                            <input className='form-control form-control-sm' type='text' inputMode='numeric' style={{ width: 70 }}
                                value={destCol} onChange={(e) => { setDestCol(e.target.value.replace(/\D/g, '')); setPreview(null); }} />
                        </div>
                    </div>
            <small className='text-muted d-block mb-3' style={{ fontSize: 11 }}>
                        1-based column numbers. Defaults to 1 (source) and 2 (destination).
                    </small>

                    <label className='d-block mb-2 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Overwrite</label>
                    <FlexInputBuilder
                        layout='grid'
                        inputSize='sm'
                        style={{ marginBottom: '0.75rem' }}
                        inputs={[
                            {
                                name: 'overwriteMode', type: 'reactselect', size: 12,
                                value: overwrite, onChange: (v) => setOverwrite(v ?? 0),
                                isSearchable: false,
                                options: [{ label: 'Only update unmapped rows', value: 0 }, { label: 'Allow overwriting existing mappings', value: 1 }],
                            },
                        ]} />

            {/* ── Paste area ── */}
                    <label className='d-block mb-2 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Mappings</label>
                    <textarea
                        className='form-control'
                        style={{ height: 200, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                        placeholder={'Source Value\tDestination Value\nPending\tWaiting Confirmation'}
                        onKeyDown={handleTabOnKeyDown}
                        {...actionRegister('loadMappingsInput' + sourceResourceId, { onChange: () => setPreview(null) })}
                    />

                    {/* ── Preview ── */}
                    <div className='d-flex align-items-center gap-3 mt-3'>
                        <ButtonBuilder label='Preview' className='btn-outline-info btn-sm' type='button' useSpinner={false} onClick={handlePreview} />
            {preview && (
                            <div style={{ fontSize: 13 }}>
                                <span className='text-success me-3'>✓ {preview.toUpdate.length} to update</span>
                                {preview.toSkip.length > 0 && (
                                    <span className='text-warning'>⚠ {preview.toSkip.length} skipped (no destination)</span>
                                )}
                            </div>
                        )}
                    </div>
                    {preview?.toUpdate[0] && (
                        <div className='text-muted mt-2 mb-0' style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {preview.toUpdate.slice(0, 3).map((p, i) => (
                                <div key={i}>"{p.source}" → "{p.destination}"</div>
                            ))}
                            {preview.toUpdate.length > 3 && <div>… and {preview.toUpdate.length - 3} more</div>}
                            {columnCount() > 0 && <div>{columnCount()} column(s) detected</div>}
                        </div>
                    )}
                </Form>
            }
        />
    );
};

// ─── Export Mappings Modal ───────────────────────────────────────────

// Copy value mappings in a chosen shape for pasting into actions/spreadsheets.
const ExportMappingsModal = ({ sourceResourceId, sourceResourceName, fullData = [] }) => {
    const [delimiter, setDelimiter] = useState('comma'); // 'tab' | 'comma'
    const [content, setContent] = useState('ids');       // 'values' | 'ids'
    const [statusFilter, setStatusFilter] = useState('Approved');
    const [includeDest, setIncludeDest] = useState(false);

    // Distinct statuses present in this resource's mappings, for the filter options.
    const statusOptions = useMemo(() => {
        const seen = [...new Set((fullData || []).map(m => m.status).filter(Boolean))].sort();
        return [{ label: 'All statuses', value: 'all' }, ...seen.map(s => ({ label: s, value: s }))];
    }, [fullData]);

    const sep = delimiter === 'comma' ? ',' : '\t';

    // Matched rows for the current status filter, source value first.
    const matched = useMemo(() => {
        const srcKey = content === 'ids' ? 'source_value_id' : 'source_value';
        const dstKey = content === 'ids' ? 'destination_value_id' : 'destination_value';
        return (fullData || [])
            .filter(m => statusFilter === 'all' ? true : m.status === statusFilter)
            .map(m => ({
                src: m[srcKey] == null ? '' : String(m[srcKey]),
                dst: m[dstKey] == null ? '' : String(m[dstKey]),
            }));
    }, [fullData, content, statusFilter]);

    // Source only → a single delimited list on one line (7,13 or 7⇥13).
    // Source + Destination → one source⇥destination pair per line (always tab, for spreadsheet columns).
    const output = useMemo(() => {
        if (!matched.length) return '';
        if (!includeDest) return matched.map(m => m.src).join(sep);
        return matched.map(m => `${m.src}\t${m.dst}`).join('\n');
    }, [matched, includeDest, sep]);

    const resetForm = () => {
        setDelimiter('comma');
        setContent('ids');
        setStatusFilter('Approved');
        setIncludeDest(false);
    };

    const onCopy = () => {
        if (!output) return ToastBuilder('error', 'Nothing to copy for the current filters');
        handleCopy(output);
        ToastBuilder('success', includeDest ? `Copied ${matched.length} pair(s)` : `Copied ${matched.length} value(s)`);
    };

    return (
        <ModalButtonBuilder title='Export' className='btn-outline-danger'
            modalTitle={`Export ${sourceResourceName} Mappings`}
            style={{ maxWidth: '560px' }}
            onSubmit={false}
            onCancelLabel='Close'
            onClick={resetForm}
            body={
                <div>
                    <p className='mb-3' style={{ fontSize: 13 }}>
                        Copy this resource's value mappings for pasting into mapping actions or a spreadsheet.
                    </p>

                    <FlexInputBuilder
                        layout='grid'
                        inputSize='sm'
                        style={{ marginBottom: '0.75rem' }}
                        inputs={[
                            {
                                // Delimiter only applies to the source-only list; pairs are always tab-separated.
                                label: 'Format', name: 'exportDelimiter', type: 'reactselect', size: 6,
                                value: delimiter, onChange: (v) => setDelimiter(v || 'tab'), isSearchable: false,
                                readOnly: includeDest,
                                helpText: includeDest ? 'Pairs are tab-separated' : undefined,
                                options: [{ label: 'Tab separated', value: 'tab' }, { label: 'Comma separated', value: 'comma' }],
                            },
                            {
                                label: 'Content', name: 'exportContent', type: 'reactselect', size: 6,
                                value: content, onChange: (v) => setContent(v || 'values'), isSearchable: false,
                                options: [{ label: 'Values', value: 'values' }, { label: 'Value IDs', value: 'ids' }],
                            },
                            {
                                label: 'Status', name: 'exportStatus', type: 'reactselect', size: 6,
                                value: statusFilter, onChange: (v) => setStatusFilter(v || 'all'), isSearchable: false,
                                options: statusOptions,
                            },
                            {
                                label: 'Columns', name: 'exportColumns', type: 'reactselect', size: 6,
                                value: includeDest ? 'both' : 'source', onChange: (v) => setIncludeDest(v !== 'source'), isSearchable: false,
                                options: [{ label: 'Source + Destination', value: 'both' }, { label: 'Source only', value: 'source' }],
                            },
                        ]} />

                    <div className='d-flex align-items-center justify-content-between mb-2'>
                        <label className='mb-0 text-muted' style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {includeDest
                                ? `Preview (${matched.length} pair${matched.length === 1 ? '' : 's'}) · source⇥dest per line`
                                : `Preview (${matched.length} value${matched.length === 1 ? '' : 's'}) · ${delimiter === 'comma' ? 'comma' : 'tab'}-separated list`}
                        </label>
                        <ButtonBuilder label='Copy' className='btn-outline-info btn-sm' type='button' useSpinner={false} onClick={onCopy} />
                    </div>
                    {/* Textarea holds the real delimited payload that gets copied; tabs are invisible
                        here, so the visual pairing below shows the actual delimiter explicitly. */}
                    <textarea
                        className='form-control'
                        readOnly
                        style={{ height: 160, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', whiteSpace: 'pre', overflowX: 'auto' }}
                        value={output}
                        placeholder='No mappings match the current filters.'
                    />
                    <p className='text-muted mb-0 mt-1' style={{ fontSize: 11 }}>
                        {includeDest
                            ? 'One source → destination pair per line, tab-separated (paste into two columns).'
                            : `Single ${delimiter === 'comma' ? 'comma' : 'tab'}-separated list of source values.`}
                    </p>
                </div>
            }
        />
    );
};

export { MappingWorksheetModal, LoadMappingsModal, ExportMappingsModal };
export default MappingWorksheetModal;
